import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import {
	AbstractControl,
	FormBuilder,
	FormGroup,
	UntypedFormControl,
	UntypedFormGroup,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AccessTokenResponseDto } from '@app/core/models/access-token-response-dto';
import { UserLoginDto } from '@app/core/models/user-login-dto';
import { AuthService } from '@app/core/services/auth.service';
import { LoadingIndicatorService } from '@app/core/services/loading-indicator.service';
import { LogService } from '@app/core/services/log.service';
import { BaseComponent } from '@app/shared/base-component';
import { chevronRightIcon, SVGIcon, warningTriangleIcon } from '@progress/kendo-svg-icons';
import { AccountService } from '../shared/services/account.service';
import { RequestCredentialsDto } from '../shared/models/request-credentials-dto';
import { ValidateEmailResponseDto } from '../shared/models/validate-email-response-dto';
import { NotificationService } from '@progress/kendo-angular-notification';
import { StringConstants } from '@app/shared/string-constants';

@Component({
	selector: 'kpa-signin',
	templateUrl: './signin.component.html',
	styleUrl: './signin.component.scss',
})
export class SigninComponent extends BaseComponent implements OnInit, OnDestroy {
	@ViewChild('notificationRef', { read: ViewContainerRef }) public notificationRef: ViewContainerRef;
	loginForm: FormGroup;
	public chevronRightIcon: SVGIcon = chevronRightIcon;
	public warningTriangleIcon: SVGIcon = warningTriangleIcon;

	// Login-field icons — Heroicons (mini, 20×20): identification / user-circle / key.
	// Matches the Figma spec (Company ID = heroicons-mini identification) and the Insured
	// Admin portal glyphs (user-circle, key). Embedded as custom Kendo SVGIcons so they render
	// through the existing <kendo-svg-icon> + .field-icon styling (fill inherits currentColor).
	public companyIdIcon: SVGIcon = {
		name: 'identification',
		content:
			'<path fill-rule="evenodd" d="M1 6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3H4a3 3 0 0 1-3-3V6Zm4 1.5a2 2 0 1 1 4 0 2 2 0 0 1-4 0Zm2 3a4 4 0 0 0-3.665 2.395.75.75 0 0 0 .416 1A8.98 8.98 0 0 0 7 14.5a8.98 8.98 0 0 0 3.249-.604.75.75 0 0 0 .416-1.001A4.001 4.001 0 0 0 7 10.5Zm5-3.75a.75.75 0 0 1 .75-.75h2.5a.75.75 0 0 1 0 1.5h-2.5a.75.75 0 0 1-.75-.75Zm0 6.5a.75.75 0 0 1 .75-.75h2.5a.75.75 0 0 1 0 1.5h-2.5a.75.75 0 0 1-.75-.75Zm.75-4a.75.75 0 0 0 0 1.5h2.5a.75.75 0 0 0 0-1.5h-2.5Z" clip-rule="evenodd" />',
		viewBox: '0 0 20 20',
	};
	public usernameIcon: SVGIcon = {
		name: 'user-circle',
		content:
			'<path fill-rule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-5.5-2.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0ZM10 12a5.99 5.99 0 0 0-4.793 2.39A6.483 6.483 0 0 0 10 16.5a6.483 6.483 0 0 0 4.793-2.11A5.99 5.99 0 0 0 10 12Z" clip-rule="evenodd" />',
		viewBox: '0 0 20 20',
	};
	public passwordIcon: SVGIcon = {
		name: 'key',
		content:
			'<path fill-rule="evenodd" d="M8 7a5 5 0 1 1 3.61 4.804l-1.903 1.903A1 1 0 0 1 9 14H8v1a1 1 0 0 1-1 1H6v1a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-2a1 1 0 0 1 .293-.707L8.196 8.39A5.002 5.002 0 0 1 8 7Zm5-3a.75.75 0 0 0 0 1.5A1.5 1.5 0 0 1 14.5 7 .75.75 0 0 0 16 7a3 3 0 0 0-3-3Z" clip-rule="evenodd" />',
		viewBox: '0 0 20 20',
	};
	isMobile = false;
	signInInitiated = false;
	signInActionFailed = false;
	showRememberPwd = false;
	validateEmailInitiated = false;
	showForgotPasswordForm = false;
	showResetPasswordDialog = false;
	showContactAdminDialog = false;
	invalidErrorMessage = '';
	currentEmployeeId: number;
	loginMode: 'initial' | 'checking' | 'standard' | 'dual' | 'sso' = 'initial';
	ssoInitiated = false;
	private checkedCompanyId: number | null = null;

	constructor(
		private fb: FormBuilder,
		protected readonly log: LogService,
		private readonly authService: AuthService,
		private router: Router,
		private route: ActivatedRoute,
		private breakpointObserver: BreakpointObserver,
		private loadingIndicatorService: LoadingIndicatorService,
		private accountService: AccountService,
		private notificationService: NotificationService
	) {
		super(log);
	}

	ngOnInit() {
		this.breakpointObserver.observe([Breakpoints.Handset]).subscribe((result) => {
			this.isMobile = result.matches;
			if (!this.isMobile) {
				this.isMobile = false;
			}
		});
		this.loginForm = this.fb.group({
			companyId: ['', [Validators.required, this.integerValidator]],
			username: [''],
			password: [''],
			rememberMe: [false],
		});

		// Company ID drives the progressive reveal: auto-check once 6 digits are typed, and
		// collapse back to the first step whenever the id is edited after a mode was resolved.
		this.loginForm.get('companyId')?.valueChanges.subscribe((value) => this.onCompanyIdChanged(value));

		// SSO callback failures redirect here with ?ssoError so the failure shows in the same
		// inline error banner used for password-login errors (not a transient bottom toast).
		if (this.route.snapshot.queryParamMap.get('ssoError')) {
			this.signInActionFailed = true;
			this.invalidErrorMessage = StringConstants.ssoFailedMessage;
			// Drop the flag from the URL so a refresh / back-forward doesn't re-show the banner —
			// it is a one-time notice for this redirect.
			this.router.navigate(['/signin'], { replaceUrl: true });
		}

		// The SSO redirect leaves the loading overlay up on purpose, so a page restored from the
		// bfcache — Back out of the IdP, or a cancelled sign-in — would come back still covered by
		// it. A frozen page has no request in flight, so that state is always stale.
		window.addEventListener('pageshow', this.onPageShow);
	}

	ngOnDestroy(): void {
		window.removeEventListener('pageshow', this.onPageShow);
	}

	// Arrow property so add/removeEventListener see the same bound reference.
	private onPageShow = (event: PageTransitionEvent): void => {
		if (event.persisted) {
			this.loadingIndicatorService.stopLoading();
			this.ssoInitiated = false;
		}
	};

	onContinue(): void {
		const companyIdControl = this.loginForm.get('companyId');
		companyIdControl?.markAsTouched();
		companyIdControl?.markAsDirty();
		if (!companyIdControl?.valid) return;

		const companyId = parseInt(companyIdControl.value, 10);
		this.resolveLoginMode(companyId);
	}

	// Asks the API which login options this company currently has and applies the answer.
	// Always a live call: a broker can switch a company's SSO on or off at any time, so a mode
	// resolved earlier in this session is not evidence of what is allowed now.
	private resolveLoginMode(companyId: number): void {
		this.loginMode = 'checking';

		this.authService.checkLoginMode(companyId).subscribe(
			(result) => this.applyLoginMode(companyId, result.mode),
			// On API error fall back to standard login rather than blocking the user
			() => this.applyLoginMode(companyId, 'standard')
		);
	}

	private applyLoginMode(companyId: number, mode: 'standard' | 'dual' | 'sso'): void {
		this.checkedCompanyId = companyId;
		this.loginMode = mode;

		if (mode === 'standard' || mode === 'dual') {
			this.loginForm.get('username')?.setValidators([Validators.required]);
			this.loginForm.get('password')?.setValidators([Validators.required]);
		} else {
			// SSO-only: the credential fields are not on screen, so requiring them would leave
			// the form permanently invalid.
			this.loginForm.get('username')?.clearValidators();
			this.loginForm.get('password')?.clearValidators();
		}

		this.loginForm.get('username')?.updateValueAndValidity();
		this.loginForm.get('password')?.updateValueAndValidity();
	}

	// Reacts to every Company ID edit driven by the field's valueChanges:
	//  • once a full 6-digit id is entered, auto-starts the login-mode check (no Continue click);
	//  • if the id is changed/cleared after a mode was resolved, collapses back to the first step
	//    (hides Username / Password / Sign in / SSO) immediately.
	onCompanyIdChanged(value: string): void {
		const raw = (value ?? '').toString().trim();
		const parsed = parseInt(raw, 10);

		if (this.loginMode !== 'initial' && this.loginMode !== 'checking' && parsed !== this.checkedCompanyId) {
			this.resetLoginMode();
		}

		if (this.loginMode === 'initial' && /^\d{6}$/.test(raw) && parsed !== this.checkedCompanyId) {
			this.onContinue();
		}
	}

	onCompanyIdBlur(): void {
		if (this.loginMode !== 'initial' && this.loginMode !== 'checking') {
			const currentValue = parseInt(this.loginForm.get('companyId')?.value, 10);
			if (currentValue !== this.checkedCompanyId) {
				this.resetLoginMode();
			}
		}
	}

	onSignInWithSso(): void {
		const companyId = parseInt(this.loginForm.get('companyId')?.value, 10);
		this.ssoInitiated = true;
		this.authService.initiateSso(companyId).subscribe(
			(result) => {
				window.location.href = result.redirectUrl;
			},
			(err) => {
				this.ssoInitiated = false;
				this.signInActionFailed = true;
				// SSO switched off since this page was loaded is an administrative change, not a
				// fault, and says so plainly. Anything else stays the generic "try again".
				this.invalidErrorMessage = this.isSsoDisabledError(err)
					? StringConstants.ssoDisabledMessage
					: StringConstants.ssoUnavailableMessage;
				// A broker can switch SSO off for a company while this page is open, and the
				// company may be SSO-only — in which case the screen shows nothing but the button
				// that just failed. Re-resolve the mode so the form reveals whatever sign-in is
				// available now (username/password once SSO is off) instead of dead-ending here.
				this.resolveLoginMode(companyId);
			}
		);
	}

	// The API marks a switched-off tenant with the "SSO Disabled" location, the same way the
	// password path marks "UCP Event" / "IP Event". Defensive throughout: this runs on an error
	// response whose shape is not guaranteed.
	private isSsoDisabledError(err: unknown): boolean {
		const errors = (err as { error?: { errors?: Array<{ location?: string }> } })?.error?.errors;
		return Array.isArray(errors) && errors.some((e) => (e?.location ?? '').includes('SSO Disabled'));
	}

	onSubmit(): void {
		this.showValidationMsg(this.loginForm);
		if (this.loginForm.valid) {
			this.signInInitiated = true;
			this.signInEmployee();
		}
	}

	onRequestPasswordReset() {
		this.showValidationMsg(this.loginForm);
		if (this.loginForm.valid) {
			this.validateEmailInitiated = true;
			this.validateEmail();
		}
	}

	private signInEmployee(callback?: () => void): void {
		let serviceSave = null;
		serviceSave = this.authService.signin(this.loginForm);
		serviceSave.subscribe(
			(result) => {
				this.logResult(result);

				if (result && result.accessToken) {
					// Hold the overlay across the route change — the dashboard has its own work to do
					// before anything is on screen, and dropping it here leaves the user looking at
					// the login form with no sign the sign-in worked.
					this.router.navigate(['/']).then(
						() => this.loadingIndicatorService.stopLoading(),
						() => this.loadingIndicatorService.stopLoading()
					);
				} else {
					// A 200 with no token: nothing to navigate to, so release the overlay here.
					this.loadingIndicatorService.stopLoading();
				}

				if (callback) {
					callback();
				}
				this.signInActionFailed = false;
			},
			(err) => {
				this.signInInitiated = false;
				this.loadingIndicatorService.stopLoading();
				this.signInActionFailed = true;
				this.errorResponsePredicate(err);
				err.error.errors.forEach((e) => {
					if (e.location.includes('UCP Event')) {
						this.invalidErrorMessage = StringConstants.invalidSignInMessage;
					} else if (e.location.includes('IP Event')) {
						this.invalidErrorMessage = StringConstants.whitelistMessage;
					}
				});
			},
			() => {
				this.signInInitiated = false;
			}
		);
	}

	onForgotPassword() {
		this.showForgotPasswordForm = true;
		this.signInActionFailed = false;
		this.loginForm.get('password')?.clearValidators();
		this.loginForm.get('password')?.updateValueAndValidity();
	}

	integerValidator(control: AbstractControl): ValidationErrors | null {
		if (control.value === null || control.value === undefined || control.value === '') {
			return null;
		}

		const valid = !isNaN(parseInt(control.value, 10)) && Number.isInteger(parseFloat(control.value));
		return valid ? null : { notInteger: true };
	}

	getIsRequiredLabel(value: string) {
		const control = this.loginForm.get(value);
		const errors = control ? control.errors : null;

		return errors && control.dirty;
	}

	showValidationMsg(formGroup: UntypedFormGroup) {
		for (const key in formGroup.controls) {
			if (formGroup.controls.hasOwnProperty(key)) {
				const control: UntypedFormControl = <UntypedFormControl>formGroup.controls[key];
				if (control) {
					control.markAsTouched();
					control.markAsDirty();
				}
			}
		}
	}

	validateEmail(callback?: () => void): void {
		let serviceValidate = null;
		serviceValidate = this.accountService.validateEmail(this.loginForm);
		serviceValidate.subscribe(
			(result: ValidateEmailResponseDto) => {
				this.logResult(result);

				if (result && result.isValid) {
					this.showResetPasswordDialog = true;
					this.currentEmployeeId = result.employeeId;
				} else {
					this.showContactAdminDialog = true;
				}
				if (callback) {
					callback();
				}

				this.signInActionFailed = false;
			},
			(err) => {
				this.validateEmailInitiated = false;
				this.loadingIndicatorService.stopLoading();
				this.signInActionFailed = true;
				this.invalidErrorMessage = StringConstants.invalidResetPasswordMessage;
			},
			() => {
				this.validateEmailInitiated = false;
			}
		);
	}

	onCancel() {
		this.loginForm.get('password')?.setValidators([Validators.required]);
		this.loginForm.get('password')?.updateValueAndValidity();
		this.showForgotPasswordForm = false;
		this.validateEmailInitiated = false;
		this.signInInitiated = false;
		this.signInActionFailed = false;
	}

	public closeDialog() {
		this.showResetPasswordDialog = false;
		this.validateEmailInitiated = false;
		this.showContactAdminDialog = false;
		this.signInActionFailed = false;
	}

	public resendCredentials(employeeId: number) {
		this.accountService.sendLoginCredentials(employeeId).subscribe((result) => {
			if (result) {
				this.loginForm.reset();
				this.notificationService.show({
					content: 'An account recovery email has been sent to associated email',
					hideAfter: StringConstants.hideNotificationAfter,
					position: { horizontal: 'right', vertical: 'bottom' },
					animation: { type: 'fade', duration: 400 },
					type: { style: 'success', icon: true },
				});
				this.showForgotPasswordForm = false;
			} else {
				this.notificationService.show({
					content: 'Error in sending password reset email.',
					hideAfter: StringConstants.hideNotificationAfter,
					position: { horizontal: 'right', vertical: 'bottom' },
					animation: { type: 'fade', duration: 400 },
					type: { style: 'error', icon: true },
				});
			}
			this.closeDialog();
		});
	}

	private resetLoginMode(): void {
		this.loginMode = 'initial';
		this.checkedCompanyId = null;
		this.loginForm.get('username')?.clearValidators();
		this.loginForm.get('username')?.updateValueAndValidity();
		this.loginForm.get('password')?.clearValidators();
		this.loginForm.get('password')?.updateValueAndValidity();
	}
}
